import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import api from "../../services/api";
import moment from "moment";
import "moment/locale/pt-br";

import { Course, UserEvent } from "../../models/entities/twygo";
import { User } from "../../models/entities/user";
import { StatusTwygoCoursesLabels } from "../../models/enums/StatusTwygoCourses";

import type { ColumnsType } from "antd/es/table";
import {
  Drawer,
  Space,
  Tabs,
  Table,
  Empty,
  Spin,
  message,
  Collapse,
  Typography,
  Tag,
  Progress,
} from "antd";
import {
  Button,
  CourseSelected,
  BoxActions,
  CourseDetailItem,
  CourseDetailsContainer,
  ScoreTag,
  CourseHeaderContainer,
  CourseIdContainer,
  CourseNameContainer,
  CourseProgressContainer,
  CourseStatusContainer,
  ContainerCourses,
  CourseMainInfo,
  StyledCollapse,
  BoxMainInfo,
  CustomArrow,
} from "./styles";

const { Panel } = Collapse;
const { Text } = Typography;

moment.locale("pt-br");

interface TwygoCoursesProps {
  isVisible: boolean;
  onClose: () => void;
  selectedUser: User | null;
  onCoursesUpdated?: () => void;
}

const TwygoCourses: React.FC<TwygoCoursesProps> = ({
  isVisible,
  onClose,
  selectedUser,
  onCoursesUpdated,
}) => {
  const location = useLocation();
  const isUserPage = location.pathname === "/users";
  const [courses, setCourses] = useState<Course[]>([]);
  const [userEvents, setUserEvents] = useState<UserEvent[]>([]);
  const [loadingCourseAction, setLoadingCourseAction] = useState(false);
  const [availableCourses, setAvailableCourses] = useState<Course[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<number[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const handleCollapseChange = (keys: string | string[]) => {
    setActiveKeys(Array.isArray(keys) ? keys : [keys]);
  };

  const handleFetchUserCourses = async () => {
    if (!selectedUser?.user_twygo_id) return;

    setDrawerLoading(true);
    try {
      setSelectedCourses([]);

      const userEventsResponse = await api.get(
        `/twygo/info-user-courses/${selectedUser.user_twygo_id}`
      );
      setUserEvents(userEventsResponse.data.events || []);

      const coursesResponse = await api.get("/twygo/v2/courses");
      const allCourses = coursesResponse.data.data.contents || [];
      setCourses(allCourses);

      const userCourseIds = userEventsResponse.data.events.map(
        (event: UserEvent) => event.event_id
      );
      const availableCoursesList = allCourses.filter(
        (course: Course) => !userCourseIds.includes(course.content_id)
      );
      setAvailableCourses(availableCoursesList);
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Erro ao carregar cursos do usuário"
      );
    } finally {
      setDrawerLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible && selectedUser?.user_twygo_id) {
      handleFetchUserCourses();
    } else {
      setUserEvents([]);
      setAvailableCourses([]);
      setSelectedCourses([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, selectedUser]);

  const handleAddMultipleCourses = async (courseIds: number[]) => {
    if (!selectedUser || courseIds.length === 0) return;

    setLoadingCourseAction(true);
    try {
      for (const courseId of courseIds) {
        await api.post("/twygo/add-course", {
          user_twygo_id: selectedUser.user_twygo_id,
          event_id: courseId,
        });
      }
      message.success("Cursos vinculados com sucesso!");
      handleFetchUserCourses();
      if (onCoursesUpdated) {
        onCoursesUpdated();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || "Erro ao vincular cursos");
    } finally {
      setLoadingCourseAction(false);
    }
  };

  const availableCoursesColumns: ColumnsType<Course> = [
    {
      title: "ID do Curso",
      dataIndex: "content_id",
      key: "content_id",
      width: "15%",
    },
    {
      title: "Nome do Curso",
      dataIndex: "name",
      key: "name",
      width: "65%",
    },
    {
      title: "Ações",
      key: "actions",
      width: "20%",
      render: (_, record: Course) => (
        <Button
          type="primary"
          onClick={() => handleAddMultipleCourses([record.content_id])}
          loading={loadingCourseAction}
        >
          Vincular
        </Button>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedCourses,
    onChange: (selectedRowKeys: React.Key[], selectedRows: Course[]) => {
      setSelectedCourses(selectedRowKeys as number[]);
    },
  };

  const renderUserCourses = () => {
    if (userEvents.length === 0) {
      return <Empty description="Nenhum curso vinculado ao usuário" />;
    }

    return (
      <StyledCollapse
        bordered={false}
        activeKey={activeKeys}
        onChange={handleCollapseChange}
      >
        {userEvents.map((userEvent) => {
          const course = courses.find(
            (c) => c.content_id === userEvent.event_id
          );
          const courseName = course?.name || "Curso não encontrado";

          const progressValue = userEvent.progress_score || 0;

          let statusColor = "blue";
          let statusText = StatusTwygoCoursesLabels.IN_PROGRESS;

          if (progressValue === 0) {
            statusColor = "orange";
            statusText = StatusTwygoCoursesLabels.NOT_STARTED;
          } else if (progressValue === 100) {
            statusColor = "green";
            statusText = StatusTwygoCoursesLabels.COMPLETED;
          }

          const eventId = `${userEvent.event_id}`;
          const isActive = activeKeys.includes(eventId);

          return (
            <Panel
              key={eventId}
              header={
                <CourseHeaderContainer>
                  <CourseMainInfo>
                    <BoxMainInfo>
                      <CourseIdContainer>
                        {userEvent.event_id}
                      </CourseIdContainer>
                      <CourseNameContainer>{courseName}</CourseNameContainer>
                    </BoxMainInfo>
                    <CourseStatusContainer>
                      <Tag color={statusColor}>{statusText}</Tag>
                      <CustomArrow isActive={isActive} />
                    </CourseStatusContainer>
                  </CourseMainInfo>
                  <CourseProgressContainer>
                    <Progress
                      percent={progressValue}
                      size="small"
                      status={progressValue === 100 ? "success" : "active"}
                      format={(percent) => `${percent}%`}
                    />
                  </CourseProgressContainer>
                </CourseHeaderContainer>
              }
              showArrow={false}
            >
              <CourseDetailsContainer>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <CourseDetailItem>
                    <Text strong>Progresso:</Text>
                    <ScoreTag
                      status={
                        progressValue > 0
                          ? progressValue === 100
                            ? "success"
                            : "info"
                          : "warning"
                      }
                    >
                      {progressValue > 0 ? `${progressValue}%` : "Não iniciado"}
                    </ScoreTag>
                  </CourseDetailItem>

                  <CourseDetailItem>
                    <Text strong>Pontuação Final:</Text>
                    <ScoreTag
                      status={
                        (userEvent.final_score || 0) >= 70
                          ? "success"
                          : (userEvent.final_score || 0) >= 30
                          ? "info"
                          : "warning"
                      }
                    >
                      {userEvent.final_score || 0}
                    </ScoreTag>
                  </CourseDetailItem>
                </div>
                <CourseDetailItem>
                  <Text strong>Último Acesso:</Text>
                  <Text>
                    {userEvent.last_access_at
                      ? moment(userEvent.last_access_at).format(
                          "DD/MM/YYYY [às] HH:mm"
                        )
                      : "Nunca acessado"}
                  </Text>
                </CourseDetailItem>

                {userEvent.reason && (
                  <CourseDetailItem>
                    <Text strong>Motivo:</Text>
                    <Text>{userEvent.reason}</Text>
                  </CourseDetailItem>
                )}
              </CourseDetailsContainer>
            </Panel>
          );
        })}
      </StyledCollapse>
    );
  };

  return (
    <Drawer
      title={
        drawerLoading ? (
          <Space>
            <span>
              Carregando cursos do usuário... <Spin size="small" />
            </span>
          </Space>
        ) : (
          `Cursos do Usuário: ${selectedUser?.full_name}`
        )
      }
      placement="right"
      onClose={() => {
        if (!drawerLoading && !loadingCourseAction) {
          onClose();
        }
      }}
      maskClosable={!drawerLoading && !loadingCourseAction}
      visible={isVisible}
      width={800}
      footer={null}
      closable={!drawerLoading && !loadingCourseAction}
    >
      <Spin spinning={drawerLoading}>
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Cursos Vinculados" key="1">
            <ContainerCourses>{renderUserCourses()}</ContainerCourses>
          </Tabs.TabPane>
          {!isUserPage && (
            <Tabs.TabPane tab="Cursos Disponíveis" key="2">
              <Table
                rowSelection={rowSelection}
                columns={availableCoursesColumns}
                dataSource={availableCourses}
                rowKey="content_id"
                pagination={false}
                scroll={{ y: 600 }}
                locale={{
                  emptyText: (
                    <Empty description="Nenhum curso disponível para vinculação" />
                  ),
                }}
              />
              {selectedCourses.length > 0 && (
                <BoxActions>
                  <Space>
                    <CourseSelected>
                      {selectedCourses.length} curso(s) selecionado(s)
                    </CourseSelected>
                    <Button
                      type="primary"
                      onClick={() => handleAddMultipleCourses(selectedCourses)}
                      loading={loadingCourseAction}
                    >
                      Vincular Cursos Selecionados
                    </Button>
                  </Space>
                </BoxActions>
              )}
            </Tabs.TabPane>
          )}
        </Tabs>
      </Spin>
    </Drawer>
  );
};

export default TwygoCourses;
